/**
 * Determine if a string is null or empty
 *
 * @export
 * @param value
 */
export function isNullOrEmpty(value) {
	if (!value) {
		return true
	}

	if (value === null || value === undefined) {
		return true
	}

	let trimmed = value.trim()
	if (trimmed.length === 0) {
		return true
	}
	return false
}

/**
 * Determine is null or empty avatar
 * @param {*} value
 */
export function isNullOrEmptyAvatar(value) {
	if (!value) {
		return true
	}

	if (value === null || value === undefined) {
		return true
	}

	let trimmed = value.trim()
	if (trimmed.length === 0) {
		return true
	}
	return false
}

/**
 * Determine if a string contains HTML markup (e.g. content produced by the rich text editor)
 * as opposed to legacy plain text.
 * Uses content-sniffing by design (simpler than a per-record creation-date cutoff): a legacy
 * plain-text value containing literal '<'/'>' (e.g. "revisar <pendiente>") can be misclassified
 * as HTML. Accepted trade-off.
 * @param {*} value
 */
export function containsHtmlContent(value) {
	if (isNullOrEmpty(value)) {
		return false
	}
	return /<\/?[a-z][\s\S]*>/i.test(value)
}

/**
 * Remove HTML tags from a string, leaving only its decoded text content.
 * Uses DOMParser instead of a regex so HTML entities (e.g. &nbsp;) are decoded too;
 * the parsed document is never attached to the live DOM, so it does not load
 * resources or execute scripts/event handlers.
 * @param {*} value
 */
export function stripHtmlTags(value) {
	if (isNullOrEmpty(value)) {
		return ''
	}
	const parsedDocument = new DOMParser().parseFromString(value, 'text/html')
	return parsedDocument.body.textContent || ''
}

/**
 * Normalize a material code so codes that differ only in formatting compare as equal.
 * Trims surrounding whitespace and drops leading zeros, which is how SAP codes and the
 * substitutable materials catalog end up disagreeing on the same material.
 *
 * A code made entirely of zeros normalizes to '0' rather than to an empty string, because an
 * empty key would match every code-less row. A null, undefined or blank value normalizes to
 * the empty string, which callers treat as "never correlates".
 *
 * @param {*} value
 */
export function normalizeMaterialCode(value) {
	if (isNullOrEmpty(value)) {
		return ''
	}
	let trimmed = String(value).trim()
	let withoutLeadingZeros = trimmed.replace(/^0+/, '')
	if (withoutLeadingZeros.length === 0) {
		return '0'
	}
	return withoutLeadingZeros
}

/**
 * Build the key that identifies a spare part row inside a request: the equipment serie paired with
 * the normalized material code.
 *
 * The pair is the row's identity, which is why the key carries both. A request can span several
 * equipments, and the same replacement part needed by two coolers is two legitimate rows — keying
 * by code alone would collapse them and make a second, valid add look like a duplicate.
 *
 * @param {*} serie the equipment serie the row belongs to
 * @param {*} code the material code, normalized or not
 */
export function sparePartCodeKey(serie, code) {
	return `${serie || ''}|${normalizeMaterialCode(code)}`
}

/**
 * Whether a comma-separated list of addresses can serve as notification destinations.
 *
 * An absent value is not a format error. Whether the field is required is a separate question,
 * asked elsewhere; answering both here produces a message that cannot say which of the two is
 * wrong.
 *
 * An empty element -- a trailing comma, two commas together -- is skipped rather than refused. It
 * is a typing artefact and not a wrong address, and the field's own placeholder ends in a comma,
 * so refusing it would fail a save for a reason nobody would recognise. The backend's
 * SparePartSalesRequestValidator skips them too, and the same field judged by two different rules
 * is worse than either rule on its own.
 *
 * Skipping them is not the same as accepting a field made of nothing else. A value of ',' or
 * ' , ' is not empty, so the absent-value branch above lets it through, and once every element is
 * filtered away there is nothing left to test -- an empty list satisfies every() and the field
 * would pass carrying no destination at all. That case is refused explicitly.
 *
 * @param {string} emailString the field's raw contents
 * @returns {boolean} true when every element can serve as a destination
 */
export function isValidEmailFormat(emailString) {
	if (isNullOrEmpty(emailString)) {
		return true
	}
	const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
	const emails = emailString
		.split(',')
		.map((email) => email.trim())
		.filter((email) => !isNullOrEmpty(email))
	if (emails.length === 0) {
		return false
	}
	return emails.every((email) => emailRegex.test(email))
}

/**
 * Whether a comma-separated list of phone numbers can serve as SMS destinations.
 *
 * Twin of isValidEmailFormat, and deliberately shaped like it: same split on commas, same
 * tolerance of an absent value, same tolerance of an empty element between two commas.
 *
 * The rule is digits, optionally spaced or hyphenated, because that is how people write phone
 * numbers and the sender already strips those before handing the number to the provider. What it
 * refuses is anything that is not a number at all -- which is the case this exists for. A folio
 * was saved with `26,530.45` in this field, a price with a thousands separator; split on the comma
 * it produced `26` and `530.45`, and both were texted as if they were phones. Nothing about that
 * value looks like a mistake until it is split, which is why the check has to be on the whole
 * field and not on the presence of a comma.
 *
 * An absent value is not a format error. Whether the field is required is a separate question,
 * asked elsewhere; answering both here produces a message that cannot say which of the two is
 * wrong.
 *
 * @param {string} phoneString the field's raw contents
 * @returns {boolean} true when every element can serve as a destination
 */
export function isValidPhoneListFormat(phoneString) {
	if (isNullOrEmpty(phoneString)) {
		return true
	}
	const phoneRegex = /^[0-9][0-9\s-]*$/
	const phones = phoneString
		.split(',')
		.map((phone) => phone.trim())
		.filter((phone) => !isNullOrEmpty(phone))
	// A field made only of separators is not empty, so it reaches here, and with every element
	// filtered away an empty list would satisfy every() and pass carrying no destination.
	if (phones.length === 0) {
		return false
	}
	return phones.every((phone) => phoneRegex.test(phone))
}
